import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '@/lib/auth'
import { Zap, ArrowRight } from 'lucide-react'

export default function SignUp() {
  const { signUp } = useAuth()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [workspace, setWorkspace] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [needsConfirmation, setNeedsConfirmation] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password.length < 8) { setError('Password must be at least 8 characters'); return }
    setLoading(true)
    setError('')
    try {
      const { error, needsConfirmation: needsConf } = await signUp(email, password, name, workspace)
      if (error) {
        setError(error.message || 'Something went wrong')
      } else if (needsConf) {
        // Confirmation-ON path: signUp succeeded but no session yet. The
        // workspace will be created automatically when the user returns via
        // the email confirmation link (see loadUserData repair branch in auth.ts).
        setNeedsConfirmation(true)
      } else {
        navigate('/onboarding')
      }
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '11px 14px',
    background: 'var(--hover-bg)',
    border: '1px solid var(--border-default)',
    borderRadius: 10,
    fontSize: 14,
    color: 'var(--ink-primary)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 150ms',
  }

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--ink-tertiary)',
    display: 'block',
    marginBottom: 7,
    letterSpacing: '0.02em',
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '20%', right: '25%', width: 500, height: 500, background: 'radial-gradient(circle, color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} style={{ width: '100%', maxWidth: 420, position: 'relative' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: 'var(--logo-glow)' }}>
            <Zap style={{ width: 24, height: 24, color: 'var(--accent-ink)' }} />
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: '0 0 6px' }}>Create your workspace</h1>
          <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>Get started in under a minute</p>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 20, padding: '32px' }}>
          {needsConfirmation ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'center', padding: '12px 4px' }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Check your email</h2>
              <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.5 }}>
                We sent a confirmation link to <strong style={{ color: 'var(--ink-primary)' }}>{email}</strong>. Click the link to finish creating your workspace — we'll set everything up automatically when you return.
              </p>
              <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>
                No email? Check spam, then try signing up again with a different address.
              </p>
            </div>
          ) : (
          <form onSubmit={handleSubmit} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {error && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ background: 'color-mix(in srgb, var(--danger) var(--status-tint-dim), transparent)', border: '1px solid color-mix(in srgb, var(--danger) var(--status-tint-strong), transparent)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: 'var(--danger)' }}>
                {error}
              </motion.div>
            )}

            <div>
              <label style={labelStyle}>Your name</label>
              <input
                type="text"
                name="full_name"
                autoComplete="off"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Alex Johnson"
                required
                style={inputStyle}
                onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
              />
            </div>

            <div>
              <label style={labelStyle}>Workspace name</label>
              <input
                type="text"
                name="workspace_name"
                autoComplete="off"
                value={workspace}
                onChange={e => setWorkspace(e.target.value)}
                placeholder="Acme Corp"
                required
                style={inputStyle}
                onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
              />
            </div>

            <div>
              <label style={labelStyle}>Work email</label>
              <input
                type="email"
                name="email_address"
                autoComplete="off"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="alex@acme.com"
                required
                style={inputStyle}
                onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
              />
            </div>

            <div>
              <label style={labelStyle}>Password</label>
              <input
                type="password"
                name="new_password"
                autoComplete="new-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="8+ characters"
                required
                style={inputStyle}
                onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '12px', fontSize: 14, marginTop: 4 }}
            >
              {loading ? 'Creating workspace...' : <> Create workspace <ArrowRight style={{ width: 15, height: 15 }} /></>}
            </button>
          </form>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--ink-tertiary)' }}>
          Already have an account?{' '}
          <Link to="/signin" style={{ color: 'var(--accent-text)', textDecoration: 'none', fontWeight: 600 }}>Sign in</Link>
        </p>
      </motion.div>
    </div>
  )
}
